/**
 * Все числа баланса — здесь и только здесь. Логика в соседних модулях их не
 * хардкодит, чтобы тюнинг не требовал чтения физики.
 *
 * Единицы — логические: поле WORLD_WIDTH × WORLD_HEIGHT, секунды. Экран любого
 * размера вписывает это поле целиком (с полями по краям), поэтому баланс не
 * зависит от устройства: на планшете волан не летит «выше», чем на телефоне.
 */

export const WORLD_WIDTH = 360
export const WORLD_HEIGHT = 640

export const gameConfig = {
  /** Фиксированный шаг физики. Отвязан от частоты кадров: 60 и 120 Гц играют одинаково. */
  physicsStep: 1 / 120,
  /** Кадр длиннее этого (вкладка была скрыта, лаг) не догоняется — игра «замирает». */
  maxFrameTime: 0.1,

  shuttle: {
    radius: 9,
    /** Откуда падает волан при подаче. */
    serveY: 110,
    /** Ускорение свободного падения на старте партии. */
    gravity: 900,
    /**
     * Коэффициент квадратичного сопротивления: a = −k·|v|·v. Как у настоящего
     * волана — быстро гасит скорость после удара и ограничивает скорость
     * падения. Терминальная скорость = √(gravity / k) ≈ 560.
     */
    drag: 900 / 560 ** 2,
    /** Потеря скорости при ударе о боковую стенку (доля, что остаётся). */
    wallBounce: 0.6,
  },

  paddle: {
    /**
     * Центр ракетки по вертикали. Ниже неё — место под палец, чтобы он не
     * закрывал волан. Поднята (дважды, по месту) — раньше висела в пустом
     * зазоре под кортом; теперь стоит в глубине ближней половины, между
     * передней линией подачи и лицевой (render.ts) — как игрок на своей
     * половине, а не за кортом.
     */
    y: 450,
    /**
     * Полуширина зоны отбоя (без радиуса волана). Уменьшена — на выросшем
     * корте (render.ts, COURT_SCALE) ракетка прежнего размера смотрелась
     * огромной на фоне поля. minHalfWidth (difficulty) — 30, запас на
     * сужение от серии отбоев остался.
     */
    halfWidth: 36,
    /**
     * Скорость догона пальца, 1/с. Не мгновенно — иначе скорость ракетки
     * (она влияет на угол отбоя) скачет от кадра к кадру. 40 → ~25 мс задержки.
     */
    followRate: 40,
  },

  hit: {
    /** Вертикальная скорость отбоя. Подобрана так, чтобы волан поднимался на ~⅔ поля. */
    bounceBase: 1700,
    /** Случайная добавка к bounceBase в обе стороны — чтобы удары не были одинаковыми. */
    bounceJitter: 100,
    /** Горизонтальная скорость при ударе краем ракетки (центром — ноль). */
    aimSpeed: 260,
    /** Доля скорости ракетки, которую она передаёт волану. */
    paddleCarry: 0.35,
    /** Случайный боковой снос, чтобы при идеально центральных ударах волан не стоял на месте. */
    driftJitter: 40,
  },

  /**
   * Сложность считается по числу отбоев, а не по очкам: иначе бонусы комбо
   * разгоняли бы игру тем быстрее, чем лучше играешь.
   */
  difficulty: {
    /** На сколько растёт множитель гравитации за каждый отбой. */
    gravityStep: 0.03,
    /** Потолок множителя: дальше темп не растёт. 2.0 — на 34-м отбое. */
    maxGravityMultiplier: 2.0,
    /** С какого отбоя ракетка начинает сужаться. */
    narrowFromHits: 15,
    /** На сколько сужается полуширина за отбой. */
    narrowStep: 0.8,
    /** Минимальная полуширина: уже — не игра, а лотерея. */
    minHalfWidth: 30,
  },

  combo: {
    /**
     * «Точный» удар — в центральную часть ракетки, доля полуширины. Серия
     * точных ударов и есть «темп»: удар краем её сбрасывает.
     */
    perfectZone: 0.35,
    /** Каждые step точных ударов подряд — +1 очко за удар. */
    step: 5,
    /** Потолок бонуса за удар: при 3 удар стоит максимум 4 очка. */
    maxBonus: 3,
  },

  /** Порыв ветра: боковое ускорение на несколько секунд. */
  wind: {
    /** До этого отбоя ветра нет — первые удары для разминки. */
    fromHits: 8,
    /** Шанс порыва после отбоя (если сейчас не дует). */
    chance: 0.15,
    /** Боковое ускорение, ед./с². 70 → снос ~⅓ поля за полёт. */
    force: 70,
    duration: 2.5,
  },

  /** Замедление после промаха, до экрана game over. */
  slowMo: {
    /** Во сколько раз медленнее идёт игровое время. */
    timeScale: 0.25,
    /** Сколько длится, в реальных секундах. */
    duration: 0.35,
  },

  /**
   * Падающие бонусы. Ловятся только воланом — ракетка сквозь них проходит:
   * иначе бонус «ловился бы сам», пока игрок просто отбивает.
   */
  bonus: {
    /** С какого отбоя начинают падать — первые удары на разминку. */
    fromHits: 3,
    /** Шанс появления бонуса после отбоя. */
    chance: 0.25,
    /** Больше этого на поле одновременно не бывает. */
    maxOnField: 2,
    radius: 14,
    /** Скорость падения, ед./с: поле проходит секунд за 12 — успеть можно. */
    fallSpeed: 55,
    /** Покачивание при падении — как лист, а не камень. */
    swayAmp: 18,
    swayRate: 1.6,
    /**
     * Относительная частота видов. Монета — основная, остальное — редкость.
     * Отрицательные (narrow, fast, fog, breaker) — примерно треть выпадений:
     * реже — их перестают замечать, чаще — игра становится наказанием.
     */
    weights: {
      coin: 5,
      wide: 2,
      slow: 2,
      double: 2,
      shield: 1,
      narrow: 1.5,
      fast: 1.5,
      fog: 1,
      breaker: 1,
    },
    wideFactor: 1.5,
    /** Длительности эффектов — в реальных секундах. */
    wideDuration: 8,
    /** Во сколько раз медленнее идёт игра под «замедлением». */
    slowScale: 0.6,
    slowDuration: 5,
    doubleDuration: 8,
    /** Отрицательные. Узкая ракетка — не уже narrowMinHalfWidth даже на поздней сложности. */
    narrowFactor: 0.65,
    narrowMinHalfWidth: 20,
    narrowDuration: 6,
    /** Во сколько раз быстрее идёт игра под «ускорением». */
    fastScale: 1.35,
    fastDuration: 5,
    /** Туман закрывает верх поля до этой доли высоты. */
    fogDepth: 0.55,
    fogDuration: 5,
  },

  /** Лучший счёт, с которого открывается скин (см. skins.ts). */
  unlocks: {
    sky: 15,
    night: 30,
    pearl: 50,
  },

  particles: {
    countPerHit: 7,
    life: 0.45,
    speed: 160,
  },
} as const

export type GameConfig = typeof gameConfig

/**
 * Что показывать игроку. Скины спрятаны до магазина за монеты: код и тесты
 * остаются, игра рисует классику.
 */
export const features = {
  skins: false,
} as const
